/**
 * Auto-tag heuristics shared by the worker (Bulk Sort / Bulk Import / Auto Tag
 * endpoints) and the client (Thêm bộ ảnh + Sắp xếp panels).
 *
 * Two layers of inference:
 *   1. Parent folder name → kimono_tag (e.g. "06. Furisode" → "furisode");
 *      when the parent says nothing, the set's own name is tried instead
 *      ("13. ueno furisode đỏ momiji" → "furisode").
 *   2. Photo set folder name → shoot/season/scene/color tags (e.g. "Andy Yang
 *      couple sakura sensoji đỏ" → shoot:[couple], season:[sakura],
 *      scene:[sensoji], color:[Đỏ])
 *
 * `suggestFolderTags` combines both — every import path goes through it so a
 * set is fully tagged the moment it is imported. Names the rules can't read
 * fall back to AI in the Auto Tag route (worker `services/ai-text-tags.ts`).
 *
 * Every tag emitted here MUST be an existing Airtable option (exact spelling),
 * or imports create look-alike choices. Patterns run on the folded name
 * (lower-case, no diacritics, đ → d), so write them without accents.
 */

import { INDONESIA_SHOOT_TAG } from "./tag-catalog.js";

/** Lower-case + strip Vietnamese diacritics, including đ → d (NFD leaves đ
 *  intact, so "đỏ" would otherwise never match "do"). */
export function foldVietnamese(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d");
}

/**
 * Folder name → kimono_tag tier. Checked in order on the folded name; first
 * match wins. `parentOnly` rules are too loose for a customer's set name:
 * "nam" is a common given name ("Hoài Nam couple") and "family" there means
 * the shoot type, not the family kimono category.
 */
export const KIMONO_TAG_MAP: ReadonlyArray<{ pattern: RegExp; tag: string; parentOnly?: boolean }> = [
  { pattern: /grand\s*premium/, tag: "grand premium" },
  { pattern: /cao\s*cap|premium/, tag: "premium furisode" },
  { pattern: /furisode|\bfuri\b/, tag: "furisode" },
  { pattern: /traditional|truyen\s*thong/, tag: "traditional" },
  { pattern: /\bren\b|\blace\b/, tag: "lace" },
  { pattern: /yukata/, tag: "yukata" },
  { pattern: /basic|co\s*ban/, tag: "basic" },
  { pattern: /hakama/, tag: "hakama" },
  { pattern: /family|famil|gia\s*dinh/, tag: "family", parentOnly: true },
  { pattern: /\bmen\b|\bnam\b/, tag: "men", parentOnly: true },
  { pattern: /\bmen\b|kimono\s*nam\b/, tag: "men" },
];

/** Not a tier — a size that sits next to one ("big size furisode"). */
const BIG_SIZE = /big\s*size|plus\s*size|ngoai\s*co/;

/** Match a parent (category) folder name. Returns null on no match. */
export function matchKimonoTag(folderName: string): string | null {
  const text = foldVietnamese(folderName);
  for (const { pattern, tag } of KIMONO_TAG_MAP) {
    if (pattern.test(text)) return tag;
  }
  return null;
}

/** Kimono tier from a photo set's OWN name — the fallback when the parent
 *  folder has no tier ("Trang/13. ueno furisode đỏ momiji"). */
export function matchKimonoTagInName(name: string): string | null {
  const text = foldVietnamese(name);
  for (const { pattern, tag, parentOnly } of KIMONO_TAG_MAP) {
    if (!parentOnly && pattern.test(text)) return tag;
  }
  return null;
}

/** Season phrases that contain a colour word ("cẩm tú cầu" → "cam") — removed
 *  before colour parsing. */
const SEASON_PHRASES = /cam\s*tu\s*cau/g;

/** Parse a folder name for kimono_color_tag values ("đen vàng" → "Đen vàng"). */
export function parseColorTags(name: string): string[] {
  let text = foldVietnamese(name).replace(SEASON_PHRASES, " ");
  const colors = new Set<string>();

  // Compound colors first
  if (/den\s+vang/.test(text)) {
    colors.add("Đen vàng");
    text = text.replace(/den\s+vang/, " ");
  }
  if (/trang\s+vang/.test(text)) {
    colors.add("Trắng vàng");
    text = text.replace(/trang\s+vang/, " ");
  }

  // Simple colors (Vietnamese + the English words photographers also use)
  if (/\bden\b|\bblack\b/.test(text)) colors.add("Đen");
  if (/\bdo\b|\bred\b/.test(text)) colors.add("Đỏ");
  if (/\bhong\b|\bpink\b/.test(text)) colors.add("Hồng");
  if (/\btrang\b|\bwhite\b/.test(text)) colors.add("Trắng");
  if (/xanh\s*la|\bgreen\b/.test(text)) colors.add("Xanh lá");
  else if (/xanh\s*duong|\bxanh\b|\bblue\b/.test(text)) colors.add("Xanh dương");
  if (/\bvang\b|\byellow\b/.test(text)) colors.add("Vàng");
  if (/\btim\b|\bpurple\b/.test(text)) colors.add("tím");
  if (/\bcam\b|\borange\b/.test(text)) colors.add("cam");

  return [...colors];
}

/** Parse a photo set folder name for shoot/season/scene/color tags. */
export function parseSubfolderTags(name: string): {
  shootTags: string[];
  seasonTags: string[];
  sceneTags: string[];
  colorTags: string[];
} {
  const text = foldVietnamese(name);
  const shootTags: string[] = [];
  const seasonTags: string[] = [];
  const sceneTags: string[] = [];

  // shoot_tag
  if (/couple|cap\s*doi|kimono\s*doi/.test(text)) shootTags.push("couple");
  if (/family|famil|gia\s*dinh/.test(text)) shootTags.push("family");
  if (/indonesia|\bindo\b|hijab|trum\s*khan|khan\s*trum/.test(text)) {
    shootTags.push(INDONESIA_SHOOT_TAG);
  }
  if (/studio/.test(text)) shootTags.push("Studio");

  // season_tag
  if (/sakura|hoa\s*anh\s*dao/.test(text)) seasonTags.push("sakura");
  if (/momiji/.test(text)) seasonTags.push("momiji");
  if (/summer|mua\s*he/.test(text)) seasonTags.push("summer");
  if (/cam\s*tu\s*cau|ajisai|hydrangea/.test(text)) seasonTags.push("cẩm tú cầu");
  if (/phao\s*hoa|hanabi|firework/.test(text)) seasonTags.push("pháo hoa");
  if (/hello\s*kitty/.test(text)) seasonTags.push("Hello Kitty");

  // scene_tag
  if (/night|ban\s*dem|\bdem\b/.test(text)) sceneTags.push("night");
  if (/sensoji|senso\s*ji/.test(text)) sceneTags.push("sensoji");
  if (/nezu/.test(text)) sceneTags.push("nezu");
  if (/skytree/.test(text)) sceneTags.push("skytree");
  if (/sumida/.test(text)) sceneTags.push("sumida park");
  if (/ueno/.test(text)) sceneTags.push("ueno");
  if (/hirai/.test(text)) sceneTags.push("hirai");

  return { shootTags, seasonTags, sceneTags, colorTags: parseColorTags(name) };
}

export interface FolderNameTags {
  /** Tier (at most one) plus "big size" when the name says so. */
  kimonoTags: string[];
  shootTags: string[];
  seasonTags: string[];
  sceneTags: string[];
  colorTags: string[];
}

/**
 * Every tag a set's folder names imply. The parent (category) folder wins for
 * the kimono tier — it is the admin's deliberate choice — and the set name is
 * the fallback.
 */
export function suggestFolderTags(setName: string, parentName?: string | null): FolderNameTags {
  const tier = (parentName ? matchKimonoTag(parentName) : null) ?? matchKimonoTagInName(setName);
  const kimonoTags = tier ? [tier] : [];
  if (BIG_SIZE.test(foldVietnamese(`${parentName ?? ""} ${setName}`))) kimonoTags.push("big size");
  return { kimonoTags, ...parseSubfolderTags(setName) };
}
